"use client";

import type { SupplierEvaluationProject, EvaluationStep } from "../types";
import { STEP_LABELS, STEP_DESCRIPTIONS, getStepFromState } from "../types";

interface Props {
  project: SupplierEvaluationProject;
  activeStep: EvaluationStep;
  onStepClick: (step: EvaluationStep) => void;
  onBack: () => void;
}

const STEPS = [1, 2, 3, 4, 5] as const;

export function EvaluationSidebar({ project, activeStep, onStepClick, onBack }: Props) {
  const currentStep = getStepFromState(project.workflow_state);

  return (
    <div style={{
      width: "240px",
      flexShrink: 0,
      background: "var(--paper-2)",
      borderRight: "1px solid var(--line)",
      display: "flex",
      flexDirection: "column",
      height: "100%",
    }}>
      {/* Header */}
      <div style={{ padding: "16px", borderBottom: "1px solid var(--line)" }}>
        <button
          onClick={onBack}
          style={{
            display: "flex", alignItems: "center", gap: "6px",
            background: "none", border: "none", color: "var(--ink-mute)",
            fontSize: "13px", cursor: "pointer", marginBottom: "12px",
            padding: 0,
          }}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/>
          </svg>
          Back
        </button>
        <p style={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase", color: "var(--ink-mute)", marginBottom: "4px" }}>
          Supplier Evaluation
        </p>
        <p style={{ fontSize: "14px", fontWeight: 600, color: "var(--ink)", lineHeight: 1.3, wordBreak: "break-word" }}>
          {project.project_name}
        </p>
        <p style={{ fontSize: "12px", color: "var(--ink-mute)", marginTop: "2px" }}>
          {project.supplier_info.company_name}
        </p>
      </div>

      {/* Steps */}
      <div style={{ flex: 1, overflowY: "auto", padding: "8px" }}>
        {STEPS.map((step) => {
          const isActive  = step === activeStep;
          const isDone    = step < activeStep;
          const isLocked  = step > Math.max(currentStep, activeStep);

          return (
            <button
              key={step}
              onClick={() => !isLocked && onStepClick(step)}
              disabled={isLocked}
              style={{
                width: "100%", textAlign: "left",
                padding: "10px 12px", borderRadius: "8px",
                border: isActive ? "1px solid rgba(91,108,255,0.35)" : "1px solid transparent",
                background: isActive ? "rgba(91,108,255,0.10)" : "transparent",
                cursor: isLocked ? "default" : "pointer",
                marginBottom: "2px",
                opacity: isLocked ? 0.4 : 1,
                transition: "all .15s ease",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <span style={{
                  width: "22px", height: "22px", borderRadius: "50%",
                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                  fontSize: "11px", fontWeight: 700, flexShrink: 0,
                  background: isDone
                    ? "var(--indigo)"
                    : isActive
                      ? "rgba(91,108,255,0.2)"
                      : "var(--paper-3)",
                  color: isDone || isActive ? "#fff" : "var(--ink-mute)",
                  border: isActive ? "1px solid rgba(91,108,255,0.5)" : "1px solid transparent",
                }}>
                  {isDone
                    ? <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                    : step}
                </span>
                <span style={{
                  fontSize: "13px",
                  fontWeight: isActive ? 600 : 400,
                  color: isActive ? "var(--ink)" : "var(--ink-soft)",
                  lineHeight: 1.3,
                }}>
                  {STEP_LABELS[step]}
                </span>
              </div>
              {isActive && (
                <p style={{ fontSize: "11px", color: "var(--ink-mute)", marginTop: "3px", marginLeft: "30px" }}>
                  {STEP_DESCRIPTIONS[step]}
                </p>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
